import type * as DialogPrimitive from "@radix-ui/react-dialog";
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  PropsWithChildren,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import { TButtonProps, TButtonWidth } from "../../primitives";
import { DefinedDefaults, SubsetKeys, subsetOf } from "@/ui/lib";
import { TTypographyAlign } from "../../typography/typography.config";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const DIALOG_CLOSE_ICON: TIconKey = "cross";

export const AVAILABLE_ALIGNMENTS =
  subsetOf<TTypographyAlign>()([ "left", "center" ]);

export const DIALOG_TYPOGRAPHY_ALIGN_PROPS: Record<TDialogAlignContent, TDialogAlignContent> = {
  left: "left",
  center: "center",
} as const;

export const DIALOG_FOOTER_CTAS_DIRECTION_CLASS_MAP: Record<TDialogAlignContent, string> = {
  left: "flex-row-reverse justify-start",
  center: "flex-col justify-center",
} as const;

export const DIALOG_FOOTER_CTA_DIRECTION_BUTTON_SIZE_MAP: Record<
  TDialogAlignContent, TButtonWidth
> = {
  left: "content",
  center: "fill",
};

/*-------------------------------------
|  Types
-------------------------------------*/

export type TDialogAlignContent = SubsetKeys<typeof AVAILABLE_ALIGNMENTS>;

export type TAlignProps = {
  align?: TDialogAlignContent;
};

export type TDialogRootProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Root> &
  PropsWithChildren &
  TAlignProps & {
    title: string;
    description: string;
    footer?: TDialogFooterProps;
  };
export type TDialogFooterCtas = {
  primary?: TButtonProps;
  secondary?: TButtonProps
};
export type TDialogFooterCtaProps = TAlignProps & {
  ctas?: TDialogFooterCtas;
};
export type TDialogTriggerElement = ComponentRef<typeof DialogPrimitive.Trigger>;
export type TDialogTriggerProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>;
export type TDialogPortalProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>;
export type TDialogOverlayElement = ComponentRef<typeof DialogPrimitive.Overlay>;
export type TDialogOverlayProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;
export type TDialogContentElement = ComponentRef<typeof DialogPrimitive.Content>;
export type TDialogContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content>;
export type TDialogCloseElement = ComponentRef<typeof DialogPrimitive.Close>;
export type TDialogCloseProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Close>;
export type TDialogTitleElement = ComponentRef<typeof DialogPrimitive.Title>;
export type TDialogTitleProps = TAlignProps & ComponentPropsWithoutRef<typeof DialogPrimitive.Title>;
export type TDialogDescriptionElement = ComponentRef<typeof DialogPrimitive.Description>;
export type TDialogDescriptionProps = TAlignProps & ComponentPropsWithoutRef<typeof DialogPrimitive.Description>;
export type TDialogHeaderProps = HTMLAttributes<HTMLDivElement>;
export type TDialogFooterProps = HTMLAttributes<HTMLDivElement> & TDialogFooterCtaProps;

type TDialogRootDefaultProps = DefinedDefaults<
  TDialogRootProps,
  "align"
>;

type TDialogTitleDefaultProps = DefinedDefaults<
  TDialogRootProps,
  "align"
>;

type TDialogDescriptionDefaultProps = DefinedDefaults<
  TDialogRootProps,
  "align"
>;

type TDialogFooterCtaDefaultProps = DefinedDefaults<
  TDialogRootProps,
  "align"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const DIALOG_ROOT_DEFAULTS = {
  align: "left",
} satisfies TDialogRootDefaultProps;

export const DIALOG_TITLE_DEFAULTS = {
  align: "left",
} satisfies TDialogTitleDefaultProps;

export const DIALOG_DESCRIPTION_DEFAULTS = {
  align: "left",
} satisfies TDialogDescriptionDefaultProps;

export const DIALOG_FOOTER_CTAS_DEFAULTS = {
  align: "left",
} satisfies TDialogFooterCtaDefaultProps;
